/**
 * Группа полей карточки внутри сворачиваемой секции.
 */

import { CardRuntimeSectionLayout } from "@/components/card/shared";
import { DynamicField } from "./DynamicField";
import {
  sectionColorWrapperClass,
  SECTION_COLOR_HEADER_CLASS,
  SECTION_COLOR_TRIGGER_CLASS,
} from "./sectionColorPolicies";
import type {
  CardTemplateSection,
  CardTemplateField,
  CardValuePayload,
  ObjectCardFieldValue,
} from "@/types/card-template";

interface CardSectionGroupProps {
  section: CardTemplateSection | null;
  fields: CardTemplateField[];
  values: ObjectCardFieldValue[];
  errors: Record<string, string>;
  onChange: (fieldKey: string, payload: Partial<CardValuePayload>) => void;
  readonly?: boolean;
  /** Для persist open-state секций в сессии. */
  objectId?: number | null;
  /** GUID ГАР объекта для поля obj_address */
  objectGarUuid?: string | null;
}

type FieldDependencyRule = {
  depends_on?: string;
  equals?: boolean | string | number | null;
  effect?: "enable" | "disable" | "hide";
};

function readFirstActualValue(values: ObjectCardFieldValue[]): Map<string, unknown> {
  const byKey = new Map<string, unknown>();
  for (const row of values) {
    const p = row.values?.[0];
    if (!p) continue;
    if (p.value_boolean !== undefined && p.value_boolean !== null) {
      byKey.set(row.field_key, p.value_boolean);
      continue;
    }
    if (p.value_numeric !== undefined && p.value_numeric !== null) {
      byKey.set(row.field_key, p.value_numeric);
      continue;
    }
    if (p.value_date !== undefined && p.value_date !== null) {
      byKey.set(row.field_key, p.value_date);
      continue;
    }
    if (p.value_text !== undefined && p.value_text !== null) {
      byKey.set(row.field_key, p.value_text);
      continue;
    }
    if (p.kls_id !== undefined && p.kls_id !== null) {
      byKey.set(row.field_key, p.kls_id);
    }
  }
  return byKey;
}

function parseDependencyRule(field: CardTemplateField): FieldDependencyRule | null {
  const raw = field.visibility_rules as Record<string, unknown> | undefined;
  if (!raw) return null;

  const depends_on =
    typeof raw.depends_on === "string" && raw.depends_on.trim() !== ""
      ? raw.depends_on.trim()
      : undefined;
  if (!depends_on) return null;

  const effect =
    raw.effect === "disable" || raw.effect === "enable" || raw.effect === "hide"
      ? raw.effect
      : "enable";

  return {
    depends_on,
    equals:
      raw.equals === undefined
        ? true
        : (raw.equals as boolean | string | number | null),
    effect,
  };
}

function shouldDisableField(
  field: CardTemplateField,
  valuesByKey: Map<string, unknown>
): boolean {
  const rule = parseDependencyRule(field);
  if (!rule?.depends_on) return false;

  const current = valuesByKey.get(rule.depends_on);
  const expected = rule.equals;
  const isMatch =
    expected === undefined ? Boolean(current) : current === expected;

  if (rule.effect === "disable") {
    return isMatch;
  }
  // effect=enable: поле активно только когда условие выполнено
  return !isMatch;
}

function shouldHideField(
  field: CardTemplateField,
  valuesByKey: Map<string, unknown>
): boolean {
  const rule = parseDependencyRule(field);
  if (!rule?.depends_on || rule.effect !== "hide") return false;
  const current = valuesByKey.get(rule.depends_on);
  const expected = rule.equals;
  const isMatch =
    expected === undefined ? Boolean(current) : current === expected;
  // effect=hide: показываем поле только когда условие выполнено
  return !isMatch;
}

export function CardSectionGroup({
  section,
  fields,
  values,
  errors,
  onChange,
  readonly = false,
  objectId = null,
  objectGarUuid = null,
}: CardSectionGroupProps) {
  const sorted = [...fields].sort(
    (a, b) => (a.display_order ?? 100) - (b.display_order ?? 100)
  );

  const title = section?.name ?? "Другое";
  const isCollapsible = section?.is_collapsible ?? false;
  const defaultOpen = section?.is_default_open ?? true;
  const rawPolicy = (section?.ui_config as { color_policy?: unknown } | undefined)
    ?.color_policy;
  const colorPolicy =
    typeof rawPolicy === "string" && rawPolicy.length > 0 ? rawPolicy : undefined;

  const hasFieldError = sorted.some((field) => Boolean(errors[field.field_key]));
  const wrapperClass = sectionColorWrapperClass(colorPolicy);
  const triggerClass = SECTION_COLOR_TRIGGER_CLASS;
  const headerClass = SECTION_COLOR_HEADER_CLASS;
  const valuesByKey = readFirstActualValue(values);

  const content = (
    <div className="grid gap-4 py-3">
      {sorted.map((field) => {
        const hiddenByDependency = shouldHideField(field, valuesByKey);
        if (hiddenByDependency) return null;
        const fieldVal =
          values.find((v) => v.field_key === field.field_key) ?? null;
        const disabledByDependency = shouldDisableField(field, valuesByKey);
        return (
          <DynamicField
            key={field.field_key}
            field={field}
            value={fieldVal}
            onChange={onChange}
            error={errors[field.field_key]}
            readonly={readonly || disabledByDependency}
            objectGarUuid={objectGarUuid}
          />
        );
      })}
    </div>
  );

  return (
    <CardRuntimeSectionLayout
      title={title}
      isCollapsible={isCollapsible}
      defaultOpen={defaultOpen}
      forceOpen={hasFieldError}
      objectId={objectId}
      sectionPersistKey={section != null ? `s:${section.id}` : "orphan"}
      sectionRootClassName={wrapperClass}
      collapsibleTriggerClassName={triggerClass}
      collapsibleContentClassName="px-2"
      staticTitleClassName={headerClass}
      chevronPosition="end"
    >
      {content}
    </CardRuntimeSectionLayout>
  );
}
